// ToolbarComponent.js

import React, { useState } from 'react';
import Line from './Line';
import Boundary from './Boundary';
import EditModeManager from './EditModeManager';
import RepeatLineDialog from './RepeatLineDialog';
import { generateParallelLines } from './utils';
import jsts from 'jsts';
import API_URL from './config';
import LoadingSpinner from './LoadingSpinner';


const ToolbarComponent = ({
  lines,
  setLines,
  boundaries,
  setBoundaries,
  setSelectedBoundary,
  setSelectedBoundaryPoint,
  setHoveredBoundary,
  setHoveredBoundaryPoint,
  modes,
  setMode,
  setRepeatLine,
  repeatLine,
  onZoom,
  redo,
  undo,
  canUndo,
  canRedo,
  addToHistory,
  pixelToGeo,
  utmZone,
  exportData,
  imageName,
  onDisplayPath,
  onDisplayWorkspace,
  onDisplayNodeDict,
  setCurrentBoundary,
  setInnerBoundaryPoints,
  onClearAllMarkers,
  innerBoundaryPoints,
  extendSelectedLines,
  selectedLineCount = 0,
  handleUploadShapeFile,
  setShowNavigationPath,
  showNavigationPath,
  defineRowGroups,
  assignTypeAndGroupToSelectedLine,
  selectedLine,
  assignTypeAndGroupMode,
  setSelectedLine,
  turningRadius,
  preStraightDistance,
  minSptDistance,
  minSpsDistance,
  turnType,
  cuspStraightenDistance,
  useWorkspace,
  abLoop,
  abLoopCount,
  workspaceBuffer,
  navigationRowSpacing,
  gsd,
  onClearAllMeasurements,
  onCaptureScreenshot,
  setToast,
  setFlashingLineIds
}) => {
    const [isRepeatLineDialogOpen, setIsRepeatLineDialogOpen] = useState(false);
    const [isClearMarkersDialogOpen, setIsClearMarkersDialogOpen] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
    const [exportFileName, setExportFileName] = useState('');
    
    const [pickleUTM, setPickleUTM] = useState(null);
    const [picklePathENU, setPicklePathENU] = useState(null);
    // The drawn outer boundary in local UTM (computed at calculate time), stashed so /export
    // can persist it as <field>_boundary.pickle for the robot / mobile app to display.
    const [exportBoundaryUTM, setExportBoundaryUTM] = useState(null);

    const [eastingsUTM, setEastingsUTM] = useState([]);
    const [northingsUTM, setNorthingsUTM] = useState([]); 

    const [picklePath, setPicklePath] = useState(null);

    const [myGraphUTM, setMyGraphUTM] = useState(null);
    const [myGraphENU, setMyGraphENU] = useState(null);
    
    const [normalOperationPathUTM, setNormalOperationPathUTM] = useState(null);

    const [selectedType, setSelectedType] = useState('');
    const [isCalculatingPath, setIsCalculatingPath] = useState(false);
    const [calculateProgress, setCalculateProgress] = useState(0);


    const handleTypeAssignment = () => {
      if (selectedType && selectedLine && selectedLine.length > 0) {
        const filteredLines = lines.filter(line => line.type === selectedType);
        let nextGroupNumber;
        
        if (filteredLines.length === 0) {
          // If there are no lines with the selected type, start with group 1
          nextGroupNumber = 1;
        } else {
          const biggestGroupNumber = Math.max(...filteredLines.map(line => line.group));
          nextGroupNumber = biggestGroupNumber + 1;
        }
        
        // console.log("ToolbarComponent: nextGroupNumber:", nextGroupNumber);
        
        selectedLine.forEach(line => {
          assignTypeAndGroupToSelectedLine(selectedType, line, nextGroupNumber);
        });
        setSelectedType(''); // Reset dropdown after assignment
        setSelectedLine([]); // Clear selection after assignment
      }
    };

    // Resolve which line to use as the source for Repeat Line.
    //   0 selected   -> last non-empty line (default mode)
    //   1 selected   -> that line (arbitrary mode)
    //   >1 selected  -> null (caller must warn + abort)
    const resolveRepeatSource = () => {
        const selCount = Array.isArray(selectedLine) ? selectedLine.length : 0;
        const nonEmpty = lines.filter(line => line.points.length > 1);
        if (selCount === 0) {
            return { line: nonEmpty[nonEmpty.length - 1] || null, mode: 'last' };
        }
        if (selCount === 1) {
            const sel = selectedLine[0];
            // Prefer the live reference from `lines` (selection may be stale after snapshots)
            const live = lines.find(l => l.id === sel.id) || sel;
            if (!live || !live.points || live.points.length < 2) {
                return { line: null, mode: 'selected-invalid' };
            }
            return { line: live, mode: 'selected' };
        }
        return { line: null, mode: 'too-many' };
    };

    const flashSource = (lineId) => {
        if (!setFlashingLineIds || !lineId) return;
        setFlashingLineIds(new Set([lineId]));
        // Dialog will remain open while the flash auto-clears; that's fine.
        setTimeout(() => setFlashingLineIds(new Set()), 2000);
    };

    const handleRepeatLine = () => {
        const { line: sourceLine, mode } = resolveRepeatSource();
        if (mode === 'too-many') {
            if (setToast) setToast({ message: 'Select exactly one line to repeat, or clear selection to repeat the last line.', kind: 'warn' });
            return;
        }
        if (!sourceLine) {
            if (setToast) setToast({ message: 'No line available to repeat.', kind: 'warn' });
            return;
        }
        flashSource(sourceLine.id);
        setIsRepeatLineDialogOpen(true);
    };
    
    const handleConfirmRepeat = ({ gsd, count, distance, direction }) => {
        // console.log('handleConfirmRepeat called with:', { gsd, count, distance, direction });
        if (lines.length === 0) {
            // console.log("No lines to repeat");
            return;
        }
        // console.log('Repeating line with:', { gsd, count, distance, direction });
        const parsedGsd = parseFloat(gsd);
        const parsedCount = parseInt(count);
        const parsedDistance = parseFloat(distance);
        const parsedDirection = parseInt(direction);

        if (isNaN(parsedGsd) || isNaN(parsedCount) || isNaN(parsedDistance) || isNaN(parsedDirection)) {
            console.error('Invalid input values');
            return;
        }

        // filter out lines with zero length
        const filteredLines = lines.filter(line => line.points.length > 1); // TODO: there is a bug here. there should not be any lines with zero length. Probably a bug in the drawing code, or genrating the parallel lines or issue when clicking on the button to start drawing a line.
        // Resolve source again at confirm time so selection changes between button click and confirm are respected.
        const { line: sourceLine, mode: sourceMode } = resolveRepeatSource();
        if (!sourceLine) {
            if (setToast) {
                if (sourceMode === 'too-many') {
                    setToast({ message: 'Select exactly one line to repeat, or clear selection to repeat the last line.', kind: 'warn' });
                } else {
                    setToast({ message: 'No line available to repeat.', kind: 'warn' });
                }
            }
            setIsRepeatLineDialogOpen(false);
            return;
        }
        const parallelLines = generateParallelLines(sourceLine, parsedCount, parsedGsd, parsedDirection, parsedDistance);
        

        // check if parallel lines intersect with any other lines and clip them
        const parallelLinesClipped = parallelLines.map(line => {
            const newLine = new Line(line.points);
            newLine.clipToOtherLines(lines);
            return newLine;
        });

        let clippedLines;
        if (innerBoundaryPoints.length > 0) {
        // create a polygon from innerBoundaryPoints
        const geometryFactory = new jsts.geom.GeometryFactory();
        if (innerBoundaryPoints.length < 3) {
          // console.log("Inner boundary must have at least 3 points");
        }
        // Ensure the polygon is closed
        if (innerBoundaryPoints[0][0] !== innerBoundaryPoints[innerBoundaryPoints.length - 1][0] ||
            innerBoundaryPoints[0][1] !== innerBoundaryPoints[innerBoundaryPoints.length - 1][1]) {
          innerBoundaryPoints.push(innerBoundaryPoints[0]);
        }
        // Create polygon
        const shellCoords = innerBoundaryPoints.map(p => new jsts.geom.Coordinate(p[0], p[1]));
        const shell = geometryFactory.createLinearRing(shellCoords);
        const innerBoundaryPolygon = geometryFactory.createPolygon(shell);
        
        // check if parallel lines intersect with inner boundary and clip them
        clippedLines = parallelLinesClipped.map(line => {
            const newLine = new Line(line.points);
            newLine.clipToInnerBoundary(innerBoundaryPolygon);
            return newLine;
        });
      }

        const appended = innerBoundaryPoints.length > 0 ? clippedLines : parallelLinesClipped;
        const newLinesForHistory = [...lines, ...appended];
        setLines(newLinesForHistory);
        addToHistory({
          type: 'REPEAT_LINE',
          lines: newLinesForHistory.map(line => ({
            points: line.points,
            id: line.id,
            type: line.type,
            waypoints: line.waypoints,
            gsd: line.gsd,
            group: line.group,
          })),
        });
        setRepeatLine(true);
        setIsRepeatLineDialogOpen(false);
        EditModeManager.disableAllModes((value) => Object.keys(modes).forEach(mode => setMode(mode, value)));
        setSelectedBoundary(null);
  };
    
  // const createNewLine = () => {
  //   // console.log("Creating new line");
  //   const newLine = new Line([]);
  //   setLines(prevLines => {
  //     const updatedLines = [...prevLines, newLine];
  //     addToHistory({type: 'ADD_LINE', lines: updatedLines});
  //     return updatedLines;
  //   })
  // };
  
  const createNewBoundary = () => {
    // console.log("Creating new boundary");
    const newBoundary = new Boundary([]);
    setBoundaries(prevBoundaries => [...prevBoundaries, newBoundary]);
  };

  const handleModeToggle = (modeName) => {
    const currentMode = modes[modeName];
    const newMode = !currentMode;
    
    setMode(modeName, newMode);

    if (newMode) {
      if (modeName === 'drawingMode') {
        // createNewLine();
        // console.log("creating new line in handleModeToggle");
      } else if (modeName === 'drawingBoundaryMode') {
        createNewBoundary();
      } 
    } else {
      if (modeName === 'drawingBoundaryMode') {
        setCurrentBoundary(null);
      }
    }
    if (modeName === 'editBoundaryPointMode' && !newMode) {
      setSelectedBoundaryPoint(null);
    }

};

  const getDefaultExportFileName = () => {
    if (!imageName) {
      return 'exported_data.zip';
    }
    const sanitizeMetricForFileName = (value) => String(value).replace(/\./g, '');
    const defaultTurningRadius = turningRadius ? turningRadius : 4.5;
    const defaultPreStraightDistance = preStraightDistance ? preStraightDistance : 5;
    const defaultGsd = gsd ? gsd : 0;
    const trNamePart = sanitizeMetricForFileName(defaultTurningRadius);
    const psNamePart = sanitizeMetricForFileName(defaultPreStraightDistance);
    // GSD arrives from the TIFF as a long float (e.g. 2.9385716...), which made the
    // filename unreadable. Keep only the first 4 digits — enough to tell exports apart.
    const gsdNamePart = sanitizeMetricForFileName(defaultGsd).slice(0, 4);
    return `${imageName.split('.')[0]}_TR${trNamePart}_PS${psNamePart}_GSD${gsdNamePart}.zip`;
  };

  const handleOpenExportDialog = () => {
    setExportFileName(getDefaultExportFileName());
    setIsExportDialogOpen(true);
  };

  const handleExportData = async (selectedFileName) => {
  // console.log("handleExportData called");
  try {

    // adding dummy to the pickle

    // console.log("dummyPickle in handleExportData in line 223: ", pickle);
    // console.log("myGraph in handleExportData in line 225: ", myGraph);


    const response = await fetch(`${API_URL}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pickleData: pickleUTM,
        imageName: imageName,
        graph_UTM: myGraphUTM,
        normalOperationPath: normalOperationPathUTM,
        exportFileName: selectedFileName,
        turningRadius: turningRadius,
        preStraightDistance: preStraightDistance,
        gsd: gsd,
        boundary: exportBoundaryUTM   // persisted as <field>_boundary.pickle (null if none drawn)
      }),
      credentials: 'include',
      mode: 'cors'
    });
    if (response.ok) {
      // console.log('response in handleExportData in line 151:', response);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const normalizedFileName = (selectedFileName || getDefaultExportFileName()).trim();
      link.download = normalizedFileName.toLowerCase().endsWith('.zip')
        ? normalizedFileName
        : `${normalizedFileName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      console.error('Server responded with an error');
    }
  } catch (error) {
    console.error('Error exporting data:', error);
  }
};

const handleConfirmExport = () => {
  const trimmedName = exportFileName.trim();
  if (!trimmedName) {
    return;
  }
  if (!pickleUTM || !myGraphUTM || !normalOperationPathUTM) {
    alert('Please click Display Path and wait for completion before exporting data.');
    return;
  }
  setIsExportDialogOpen(false);
  handleExportData(trimmedName);
};


const handleClearMarkers = () => {
    setIsClearMarkersDialogOpen(true);
};

// const handleDeleteLine = () => {
//     setLines([]);
//     setBoundaries([]);
//     setSelectedBoundary(null);
//     setSelectedBoundaryPoint(null);
//     setHoveredBoundary(null);
//     EditModeManager.disableAllModes((value) => Object.keys(modes).forEach(mode => setMode(mode, value)));
//     setIsClearMarkersDialogOpen(false);
//     // addToHistory({ type: 'DELETE_LINE', lines: lines.filter(line => !selectedLines.includes(line)) });
// };

const handleConfirmClearMarkers = () => {
    // Delegate state wipe + history snapshot to the parent so the clear is undoable.
    if (onClearAllMarkers) onClearAllMarkers();
    EditModeManager.disableAllModes((value) => Object.keys(modes).forEach(mode => setMode(mode, value)));
    setIsClearMarkersDialogOpen(false);
    setShowNavigationPath(false);
    onDisplayPath(null);
    onDisplayWorkspace(null);
    onDisplayNodeDict(null);
};

const modeText = (modeName) => {
    if (modeName === 'drawingMode') {
        return 'Draw Lines';  
    } else if (modeName === 'drawingBoundaryMode') {
        return 'Draw Boundaries';
    } else if (modeName === 'editBoundaryPointMode') {
        return 'Edit Boundary Points';
    } else if (modeName === 'editLineMode') {
        return 'Edit Lines';
    } else if (modeName === 'editBoundaryMode') {
        return 'Edit Boundaries';
    } else if (modeName === 'editLinePointMode') {
        return 'Edit Line Points';
    } else if (modeName === 'deleteLineMode') {
        return 'Delete Lines';
    } else if (modeName === 'assignTypeAndGroupMode') {
        return 'Assign Types & Groups';
    } else if (modeName === 'measureDistanceMode') {
        return 'Measure Distance';
    }
};

const handleDisplayPath = async () => {
  if (showNavigationPath) {
    setShowNavigationPath(false);
    return;
  } else {
    setShowNavigationPath(true);
  }
  if (typeof pixelToGeo !== 'function') {
    alert('Unable to export data at this time. Please try again later.');
    return;
  }
  if (!utmZone) {
    alert('UTM Zone is not set. Unable to export data.');
    return;
  }

  const noZeroLengthLines = lines.filter(line => line.waypoints.length > 1);

  const convertedLines = noZeroLengthLines.map(line => ({
    ...line,
    points: line.points.map(point => {
      const [easting, northing] = pixelToGeo(point[0], point[1]);
      return [easting, northing];
    }),
    waypoints: line.waypoints.map(point => {
      const [easting, northing] = pixelToGeo(point[0], point[1]);
      return [easting, northing];
    })
  }));

  const optimizedLines = sortAndOptimizeLines(convertedLines);
  const filteredLinesUTM = optimizedLines.filter(line => line.waypoints.length > 1);
  const firstPointUTM = filteredLinesUTM[0].waypoints[0];
  const referenceEastingUTM = firstPointUTM[0];
  const referenceNorthingUTM = firstPointUTM[1];
  setEastingsUTM([referenceEastingUTM]);
  setNorthingsUTM([referenceNorthingUTM]);

  // Convert the user-drawn OUTER boundary (the black polygon, isInnerBoundary=false) from
  // pixels -> UTM -> local frame (UTM minus reference, same frame as pickle_data / the path)
  // so the backend can respect THIS boundary for the tight-corner maneuver, instead of an
  // approximation derived from the rows. Sends null if no outer boundary was drawn.
  const outerBoundary = (boundaries || []).find(
    b => b && !b.isInnerBoundary && Array.isArray(b.points) && b.points.length >= 3);
  const boundaryLocalUTM = outerBoundary
    ? outerBoundary.points.map(pt => {
        const [easting, northing] = pixelToGeo(pt[0], pt[1]);
        return [easting - referenceEastingUTM, northing - referenceNorthingUTM];
      })
    : null;
  setExportBoundaryUTM(boundaryLocalUTM);   // stash so /export can persist it

  const insideRows = new Set();
  const endRows = new Set();
  filteredLinesUTM.forEach(line => {
    if (line.type === 'inside_row') {
      insideRows.add(line.group);
    } else if (line.type === 'end_row') {
      endRows.add(line.group);
    }
  });
  const totalInsideRowGroups = Array.from(insideRows).length;
  const totalEndRowGroups = Array.from(endRows).length;

  const insideRowGroupIds = Array.from({ length: totalInsideRowGroups }, (_, i) => i + 1);
  const endRowGroupIds = Array.from({ length: totalEndRowGroups }, (_, i) => i + 1);
  const turnRowGroupIds = [];

  insideRowGroupIds.forEach(groupNumber => {
    insideRowGroupIds[groupNumber - 1] = filteredLinesUTM
      .filter(line => line.type === 'inside_row' && line.group === groupNumber)
      .map(line => line.id);
  });

  endRowGroupIds.forEach(groupNumber => {
    endRowGroupIds[groupNumber - 1] = filteredLinesUTM
      .filter(line => line.type === 'end_row' && line.group === groupNumber)
      .map(line => line.id);
  });

  filteredLinesUTM.forEach(line => {
    if (line.type === 'turn_row') {
      turnRowGroupIds.push(line.id);
    }
  });

  const pickleDataUTM = [
    [utmZone, referenceEastingUTM, referenceNorthingUTM, totalEndRowGroups, totalInsideRowGroups],
    ...filteredLinesUTM.map(line => 
      line.waypoints.map(point => [
        point[0] - referenceEastingUTM,
        point[1] - referenceNorthingUTM,
        line.type === 'inside_row' ? 1 : 0,
      ])
    )
  ];

  setPickleUTM(pickleDataUTM);
  setIsCalculatingPath(true);
  setCalculateProgress(0);

  const data = {
    pickle_data: pickleDataUTM,
    lines: filteredLinesUTM,
    endrow_groups: endRowGroupIds,
    inside_row_groups: insideRowGroupIds,
    turnrow_groups: turnRowGroupIds,
    field_name: imageName.split('.')[0],
    turning_radius: turningRadius ? turningRadius : 4.5,
    pre_straight_distance: preStraightDistance ? preStraightDistance : 5,
    // Omitted (null) => backend keeps the otto.yaml value. Sent only when the operator
    // has actually set one, so a blank field never silently overrides the config.
    min_spt_distance: (minSptDistance === null || minSptDistance === undefined || minSptDistance === '') ? null : minSptDistance,
    min_sps_distance: (minSpsDistance === null || minSpsDistance === undefined || minSpsDistance === '') ? null : minSpsDistance,
    // Blank => omit both, so otto.yaml decides. Choosing a turn type also engages hammer
    // mode (dubins_override=1), because reeds_shepp_override is only consulted there —
    // otherwise picking "Dubins" would silently do nothing on a yaml set to the legacy pipeline.
    dubins_override: turnType ? 1 : null,
    reeds_shepp_override: turnType === 'reeds_shepp' ? 1 : (turnType === 'dubins' ? 0 : null),
    cusp_straighten_distance: (cuspStraightenDistance === null || cuspStraightenDistance === undefined || cuspStraightenDistance === '') ? null : cuspStraightenDistance,
    use_workspace: useWorkspace ? useWorkspace : false,
    workspace_buffer: useWorkspace && workspaceBuffer ? workspaceBuffer : 1,
    ab_loop: abLoop ? abLoop : false,
    ab_loop_count: abLoopCount || 1,
    navigation_row_spacing: navigationRowSpacing ? navigationRowSpacing : 3,
    boundary: boundaryLocalUTM   // user-drawn outer boundary in local UTM (null if none)
  };

  // console.log('Data being sent for initialization:', data);
  // console.log('Data size:', JSON.stringify(data).length);

  try {
    const response = await fetch(`${API_URL}/initialize_calculation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to initialize calculation. Status: ${response.status}, Error: ${errorText}`);
      throw new Error(`Failed to initialize calculation: ${response.status} ${response.statusText}`);
    }

    const { calculationId } = await response.json();
    console.log(`Received calculation ID: ${calculationId}`);
    await new Promise(resolve => setTimeout(resolve, 1000));

    const eventSource = new EventSource(`${API_URL}/calculate_path/${calculationId}`);

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.progress !== undefined) {
        setCalculateProgress(data.progress);
      } else if (data.result) {
        const {path_UTM, graph_UTM, workspace, node_dict_UTM, master_v4_waypoints_list} = data.result;


        setMyGraphUTM(graph_UTM);
        setNormalOperationPathUTM(master_v4_waypoints_list);

        const workspaceWithCoordinates = workspace ? workspace.map(segment => 
          segment.map(point => [point[0] + referenceEastingUTM, point[1] + referenceNorthingUTM])
        ) : null;

        const nodeDictWithCoordinates = node_dict_UTM.map(node => ({
          ...node,
          x: node.x + referenceEastingUTM,
          y: node.y + referenceNorthingUTM
        }));

        if (onDisplayNodeDict && typeof onDisplayNodeDict === 'function') {
          onDisplayNodeDict(nodeDictWithCoordinates);
        } else {
          console.error('onDisplayNodeDict is not a function or is undefined');
        }
        
        if (onDisplayWorkspace && typeof onDisplayWorkspace === 'function') {
          onDisplayWorkspace(workspaceWithCoordinates);
        } else {
          console.error('onDisplayWorkspace is not a function or is undefined');
        }

        const pathWithCoordinates = path_UTM.map(segment => {
          if (Array.isArray(segment)) {
            return segment.map(point => point.map(coord => [coord[0] + referenceEastingUTM, coord[1] + referenceNorthingUTM]));
          } else if (segment.waypoints) {
            return {
              ...segment,
              waypoints: segment.waypoints.map(point => ({
                x: point[0] + referenceEastingUTM,
                y: point[1] + referenceNorthingUTM
              }))
            };
          }
          return segment;
        });
        
        if (onDisplayPath && typeof onDisplayPath === 'function') {
          onDisplayPath(pathWithCoordinates);
        } else {
          console.error('onDisplayPath is not a function or is undefined');
        }

        eventSource.close();
        setIsCalculatingPath(false);
        setCalculateProgress(0);
      } else if (data.error) {
        console.error('Error calculating path:', data.error);
        alert(`Error calculating path: ${data.error}`);
        eventSource.close();
        setIsCalculatingPath(false);
        setCalculateProgress(0);
      }
    };

    eventSource.onerror = (error) => {
      console.error('EventSource failed:', error);
      console.error('EventSource readyState:', eventSource.readyState);
      console.error('EventSource URL:', eventSource.url);
      eventSource.close();
      setIsCalculatingPath(false);
      setCalculateProgress(0);
    };

  } catch (error) {
    console.error('Error initializing calculation:', error);
    setIsCalculatingPath(false);
    setCalculateProgress(0);
    alert(`Error initializing calculation: ${error.message}`);
  }
};

const sortAndOptimizeLines = (lines) => {
  if (lines.length <= 1) return lines;

  const firstLine = lines[0];
  if (!firstLine.waypoints || firstLine.waypoints.length === 0) {
    console.error('First line has no waypoints');
    return lines;
  }

  let sortedLines = [firstLine];
  let lastPoint = firstLine.waypoints[firstLine.waypoints.length - 1];

  const restOfLines = lines.slice(1);

  while (restOfLines.length > 0) {
    let nextLineIndex = 0;
    let minDistance = Infinity;
    let shouldReverse = false;

    restOfLines.forEach((line, index) => {
      const distanceToStart = calculateDistance(lastPoint, line.waypoints[0]);
      const distanceToEnd = calculateDistance(lastPoint, line.waypoints[line.waypoints.length - 1]);
      
      if (distanceToStart < minDistance) {
        minDistance = distanceToStart;
        nextLineIndex = index;
        shouldReverse = false;
      }
      
      if (distanceToEnd < minDistance) {
        minDistance = distanceToEnd;
        nextLineIndex = index;
        shouldReverse = true;
      }
    });

    let nextLine = restOfLines.splice(nextLineIndex, 1)[0];
    if (shouldReverse) {
      nextLine.waypoints.reverse();
    }
    
    sortedLines.push(nextLine);
    lastPoint = nextLine.waypoints[nextLine.waypoints.length - 1];
  }

  return sortedLines;
};

// const reverseEveryOtherLine = (lines) => {
//   return lines.map((line, index) => index % 2 === 0 ? {...line, waypoints: line.waypoints.reverse()} : line);
// };

const calculateDistance = (point1, point2) => {
  if (!Array.isArray(point1) || point1.length < 2 || !Array.isArray(point2) || point2.length < 2) {
    console.error('Invalid point format');
    return Infinity;
  }
  const dx = point1[0] - point2[0];
  const dy = point1[1] - point2[1];
  return Math.sqrt(dx * dx + dy * dy);
};


// console.log('calculateProgress in ToolbarComponent:', calculateProgress);

return (
    <div style={{ 
        width: '100%', 
        height: '100%', 
        backgroundColor: 'lightgrey', 
        padding: '10px', 
        borderLeft: '1px solid #a0d6a0',
        boxSizing: 'border-box'
    }}>
        <button 
            style={{ 
                width: '90%', 
                marginBottom: '10px', 
                backgroundColor: '#FFCCCC',
                fontSize: '1rem',
                padding: '.25rem',
                margin: '.25rem',
                borderRadius: '5px'
            }}
            onClick={handleClearMarkers}
        >
            Clear Markers
        </button>
        {isClearMarkersDialogOpen && (
            <div className="confirmation-dialog" style={{ position: 'fixed', top: '50%', left: '40%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '4rem', border: '1px solid black', borderRadius: '10px', zIndex: '1000', width: '20rem', height: '3rem', fontSize: '1.25rem' }}>
                <p style={{ fontSize: '1.25rem', padding: '0rem', marginTop: '-2.5rem' }}>Are you sure you want to clear all markers?</p>
                <button onClick={handleConfirmClearMarkers} style={{ fontSize: '1rem', padding: '1rem 1rem', margin: '1rem', backgroundColor: '#f44336', borderRadius: '5px' }}>Yes</button>
                <button onClick={() => setIsClearMarkersDialogOpen(false)} style={{ fontSize: '1rem', padding: '1rem 1rem', margin: '1rem', backgroundColor: '#4CAF50', borderRadius: '5px' }}>No</button>
            </div>
        )}
        {Object.keys(modes).map(modeName => (
            <button
                key={modeName}
                style={{ width: '90%', marginBottom: '.2rem', fontSize: '1rem', padding: '.25rem', backgroundColor: modes[modeName] ? '#a0d6a0' : 'lightgrey', borderRadius: '5px' }}
                onClick={() => handleModeToggle(modeName)}
            > 
                {modes[modeName] ? 'Disable ' + modeText(modeName) : modeText(modeName)} 
            </button>
        ))}

        <div style={{ width: '90%', marginBottom: '.2rem', padding: '.25rem', display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '.5rem', borderRadius: '5px' }}>
        <select 
          value={selectedType} 
          onChange={(e) => setSelectedType(e.target.value)}
          style={{ fontSize: '.7rem', padding: '.25rem' }}
        >
          <option value="">Select Line Type</option>
          <option value="turn_row">Turn Row</option>
          <option value="inside_row">Inside Row</option>
          <option value="end_row">End Row</option>
          <option value="reset_row">Reset Row</option>
        </select>
        <button 
          onClick={handleTypeAssignment}
          disabled={!selectedType || selectedLine.length === 0}
          style={{ fontSize: '.7rem', padding: '.25rem' }}
        >
          Assign Type
        </button>
      </div>
        {(() => {
          const selCount = Array.isArray(selectedLine) ? selectedLine.length : 0;
          let label = 'Repeat Last Line';
          let title = 'Repeat the most recently drawn line';
          if (selCount === 1) {
            label = 'Repeat Selected Line';
            title = 'Repeat the currently selected line';
          } else if (selCount > 1) {
            label = 'Repeat Line';
            title = 'Select exactly one line, or clear selection to repeat the last line';
          }
          return (
            <button
              style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
              onClick={handleRepeatLine}
              title={title}
            >
              {label}
            </button>
          );
        })()}

        <RepeatLineDialog
            isOpen={isRepeatLineDialogOpen}
            onClose={() => setIsRepeatLineDialogOpen(false)}
            onConfirm={handleConfirmRepeat}
            gsd={gsd}
            sourceLabel={(() => {
              const { line, mode } = resolveRepeatSource();
              if (!line) return null;
              const idx = lines.findIndex(l => l.id === line.id);
              const human = idx >= 0 ? `line #${idx + 1}` : `line ${String(line.id).slice(0, 6)}`;
              return mode === 'selected' ? `selected ${human}` : `last ${human}`;
            })()}
        />
        {/* <button 
            style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
            onClick={handleMeasureDistance}
        >
            Measure Distance
        </button> */}
        <button 
            style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
            onClick={() => onClearAllMeasurements && onClearAllMeasurements()}
        >
            Clear Measurements
        </button>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', width: '90%', margin: '0 5px 4px' }}>
          <button
            onClick={undo}
            disabled={!canUndo}
            style={{ flex: 1, fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
          >
            Undo
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            style={{ flex: 1, fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
          >
            Redo
          </button>
        </div>
        <button
            onClick={handleOpenExportDialog}
            data-bridge="export-data"
            style={{width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
            // disabled={!myGraph || pickle.length === 0 || lines.length === 0}
        >
            Export Data
        </button>
        {isExportDialogOpen && (
            <div className="confirmation-dialog" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '2rem', border: '1px solid black', borderRadius: '10px', zIndex: 2000, width: '24rem', fontSize: '1rem',
                // The ToolbarComponent wrapper in ImageEditor sets
                // pointerEvents: 'none' to make the legacy panel non-
                // interactive on the canvas. CSS pointer-events cascades
                // even to position:fixed children, so without this override
                // the user can't click Confirm/Cancel or focus the input.
                pointerEvents: 'auto',
                boxShadow: '0 8px 28px rgba(0,0,0,0.25)' }}>
                <p style={{ margin: '0 0 0.75rem 0' }}>Rename exported zip file:</p>
                <input
                    type="text"
                    value={exportFileName}
                    onChange={(e) => setExportFileName(e.target.value)}
                    style={{ width: '100%', fontSize: '1rem', padding: '0.5rem', marginBottom: '0.75rem' }}
                />
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                    <button onClick={handleConfirmExport} style={{ fontSize: '1rem', padding: '0.6rem 1rem', backgroundColor: '#4CAF50', borderRadius: '5px' }}>Confirm</button>
                    <button onClick={() => setIsExportDialogOpen(false)} style={{ fontSize: '1rem', padding: '0.6rem 1rem', backgroundColor: '#f44336', borderRadius: '5px' }}>Discard</button>
                </div>
            </div>
        )}
        <button
          style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
          onClick={handleDisplayPath}
          data-bridge="display-path"
          disabled={lines.length === 0 || isCalculatingPath}
        >
        {isCalculatingPath
            ? `Calculating... ${calculateProgress.toFixed(0)}%`
            : 'Display Path'}
        </button>
        {(() => {
          const count = Array.isArray(selectedLine) ? selectedLine.length : (selectedLine ? 1 : 0);
          const disabled = count === 0;
          return (
            <button
              onClick={extendSelectedLines}
              disabled={disabled}
              title={disabled ? 'Select one or more lines first' : `Extend ${count} selected line(s)`}
              style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
            >
              Extend Lines{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })()}
      <button
          onClick={handleUploadShapeFile}
        style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
        >
        Upload ShapeFile
      </button>
      <button
          onClick={onCaptureScreenshot}
        style={{ width: '90%', marginBottom: '.25rem', fontSize: '1rem', padding: '.25rem', borderRadius: '5px' }}
        >
        Capture Screenshot
      </button>
    </div>
);
};

export default ToolbarComponent;